import React, { Component } from 'react'
import { scaleBand, scaleLinear } from 'd3-scale'
import Axes from './Axes'
import Bars from './Bars'
import Lines from './Lines'
import BarChartTooltip, { BarChartTooltipRow } from './BarChartTooltip'
import responsive from './ResponsiveWrapper'
import type { Data, Tick } from './index'
import type { DataPoint, UsageData } from '../../store'

const TOOLTIP_PADDING = 40

type Props = {
  parentWidth: number,
  width: number,
  height: number,
  data: Data,
  tempData: Data,
  id: string,
  sum: number,
  tick?: Tick,
  timeframe: string,
  unit: string,
  showComparison: boolean,
  showTemperature: boolean,
  usageData?: UsageData,
  contractFromId: { address: string },
  scrollable: boolean,
  // eslint-disable-next-line
  updateUsageData: (value: React.SetStateAction<any>) => void,
  maximumResolution: string,
  loading: boolean,
}

type HoveredSlot = {
  ts: string,
  x: number,
}

type State = {
  hoveredConsumptionSlot: HoveredSlot | null,
  hoveredProductionSlot: HoveredSlot | null,
}

class BarChart extends Component<Props, State> {
  state: State = {
    hoveredConsumptionSlot: null,
    hoveredProductionSlot: null,
  }

  // Refs for consumption and production graphs divs (scrollable)
  private consumptionScrollDiv = React.createRef<HTMLDivElement>()
  private productionScrollDiv = React.createRef<HTMLDivElement>()

  // Prevent infinite loop with this - when syncing the scrolls between two divs
  private isSyncingScroll = false

  // Track which listeners are currently attached
  // Prevents reattaching the same listener during re-renders
  private isConsumptionListenerAttached = false
  private isProductionListenerAttached = false

  componentDidMount() {
    this.attachScrollListeners()
  }
  // Ensure listeners are attached again if rerender
  // Prod div had some issues witout this
  componentDidUpdate() {
    this.attachScrollListeners()
  }

  componentWillUnmount() {
    this.detachScrollListeners()
  }

  private attachScrollListeners() {
    const consumptionDiv = this.consumptionScrollDiv.current
    const productionDiv = this.productionScrollDiv.current

    if (consumptionDiv && !this.isConsumptionListenerAttached) {
      consumptionDiv.addEventListener('scroll', this.handleScroll as EventListener, { passive: true })
      this.isConsumptionListenerAttached = true
    }

    if (productionDiv && !this.isProductionListenerAttached) {
      productionDiv.addEventListener('scroll', this.handleScroll as EventListener, { passive: true })
      this.isProductionListenerAttached = true
    }
  }

  private detachScrollListeners() {
    const consumptionDiv = this.consumptionScrollDiv.current
    const productionDiv = this.productionScrollDiv.current

    if (consumptionDiv && this.isConsumptionListenerAttached) {
      consumptionDiv.removeEventListener('scroll', this.handleScroll as EventListener)
      this.isConsumptionListenerAttached = false
    }

    if (productionDiv && this.isProductionListenerAttached) {
      productionDiv.removeEventListener('scroll', this.handleScroll as EventListener)
      this.isProductionListenerAttached = false
    }
  }
  /**
   * Keeps both consumption and production divs scrolled in sync.
   * When user scrolls one div, the other one mirrors its scroll position.
   */
  private handleScroll = (event: Event) => {
    if (this.isSyncingScroll) return

    const consumptionDiv = this.consumptionScrollDiv.current
    const productionDiv = this.productionScrollDiv.current
    if (!consumptionDiv || !productionDiv) return

    const scrolledDiv = event.currentTarget as HTMLElement | null
    const scrollSourceDiv = scrolledDiv === consumptionDiv ? consumptionDiv : productionDiv
    const scrollTargetDiv = scrolledDiv === consumptionDiv ? productionDiv : consumptionDiv

    const scrollLeftValue = scrollSourceDiv.scrollLeft

    this.isSyncingScroll = true

    if (scrollTargetDiv.scrollLeft !== scrollLeftValue) {
      scrollTargetDiv.scrollLeft = scrollLeftValue
    }

    this.isSyncingScroll = false
  }
  // Passed to Bars component to get hovered consumption bar for tooltip
  // The state value is used to get correct data values for the Tooltip component
  private setHoveredConsumptionSlot = (slot: HoveredSlot | null) => {
    this.setState({ hoveredConsumptionSlot: slot })
  }
  // Passed to Bars component to get hovered production bar for tooltip
  // The state value is used to get correct data values for the Tooltip component
  private setHoveredProductionSlot = (slot: HoveredSlot | null) => {
    this.setState({ hoveredProductionSlot: slot })
  }

  private getUsageDecimals = (unit: string): number => {
    return unit === 'MWh' ? 3 : 2
  }

  render() {
    const {
      data,
      tempData,
      tick,
      sum,
      id,
      timeframe,
      unit,
      parentWidth,
      width,
      height,
      showComparison,
      showTemperature,
      usageData,
      contractFromId,
      updateUsageData,
      maximumResolution,
      scrollable,
      loading,
    } = this.props

    const { hoveredConsumptionSlot, hoveredProductionSlot } = this.state

    const barMargins = { top: 60, right: 50, bottom: 60, left: 50 }

    const svgDimensions = {
      width: width && width < parentWidth ? width : Math.max(parentWidth, 250),
      height: Number(height),
    }

    const calculateBarSvgWidth = () => {
      let calculatedWidth = svgDimensions.width
      const usageChartData = usageData?.data
      if (!usageChartData) return calculatedWidth

      if (loading) {
        const consumptionBars = document.querySelector('#consumption')
        if (consumptionBars) {
          calculatedWidth = consumptionBars.getBoundingClientRect().width
          return calculatedWidth
        }
      }

      if (
        usageData?.level === 'day' ||
        usageData?.frame === 'year' ||
        usageData?.frame === 'month' ||
        (usageData?.frame === 'day' && usageData?.level === 'hour') ||
        (usageData?.frame === 'custom' && usageChartData.length < 32)
      ) {
        calculatedWidth = Math.max(calculatedWidth - barMargins.left - barMargins.right, 250)
        return calculatedWidth
      }

      if (svgDimensions.width && usageChartData.length >= 32) {
        const scaledWidth =
          (svgDimensions.width * usageChartData.length) / 32 - barMargins.left - barMargins.right
        calculatedWidth = Math.max(scaledWidth, 250)
      } else {
        calculatedWidth = Math.max(calculatedWidth - barMargins.left - barMargins.right, 250)
      }

      return calculatedWidth
    }

    const barsSvgDimensions = {
      height: Number(height),
      width: calculateBarSvgWidth(),
    }

    let maxConsumptionValue = Math.max(
      ...data.map((d: DataPoint) => Math.max(d.v, d.comparison ? d.comparison.v : 0))
    )

    if (maxConsumptionValue < 0) {
      maxConsumptionValue = 15
    }

    const maxProductionValue = Math.max(
      ...data.map((d: DataPoint) =>
        Math.max(d.vp ? d.vp : 0, d.comparison && d.comparison.vp ? d.comparison.vp : 0)
      )
    )
    // Add Y scale value ranges for consumption and production data
    const maxConsumptionScaleValue = maxConsumptionValue > 0 ? maxConsumptionValue : 15
    const maxProductionScaleValue = maxProductionValue > 0 ? maxProductionValue : 15

    const xScale = scaleBand()
      .padding(0.2)
      .domain(data.map(d => d.ts))
      .range([0, barsSvgDimensions.width - barMargins.right + 45])

    /* Consumption data yAxis scale */
    const yScale = scaleLinear()
      .domain([0, maxConsumptionScaleValue])
      .range([svgDimensions.height - barMargins.bottom, barMargins.top + TOOLTIP_PADDING])

    /* Production data yAxis scale */
    const yScale2 = scaleLinear()
      .domain([maxProductionScaleValue, 0])
      .range([svgDimensions.height - barMargins.top, barMargins.bottom])

      /* Temperature data yAxis scale */
    const yScale3 = scaleLinear()
      .domain([-40, 40])
      .range([svgDimensions.height - barMargins.bottom, barMargins.top + TOOLTIP_PADDING])
      /* Sometimes datapoints for today's temps are available while
      electricity data lags by a day. We need to ensure that we remove the extra day from
      the temp data when it's not available for electricty yet, to prevent the temperature plot
      plotting off the scale.   */
    const sliceData = (chartTempData: DataPoint[]) => {
      let nextTempData = chartTempData
      if (usageData && nextTempData.length > usageData?.data.length) {
        nextTempData = nextTempData.slice(0, usageData.data.length)
      }
      return nextTempData
    }

    const slicedTempData = sliceData(tempData)

    const hoveredConsumption = hoveredConsumptionSlot
      ? data.find((datapoint: DataPoint) => datapoint.ts === hoveredConsumptionSlot.ts)
      : undefined

    const hoveredTemperature = hoveredConsumptionSlot
      ? slicedTempData.find((datapoint: DataPoint) => datapoint.ts === hoveredConsumptionSlot.ts)
      : undefined

    const hoveredProduction = hoveredProductionSlot
      ? data.find((datapoint: DataPoint) => datapoint.ts === hoveredProductionSlot.ts)
      : undefined

    const consumptionTooltipRows: BarChartTooltipRow[] = []

    if (hoveredConsumption) {
      consumptionTooltipRows.push({
        ts: hoveredConsumption.ts,
        label: 'kulutus',
        value: hoveredConsumption.v,
        unit,
        decimals: this.getUsageDecimals(unit),
      })
    }

    if (showComparison && hoveredConsumption?.comparison) {
      consumptionTooltipRows.push({
        ts: hoveredConsumption.comparison.ts,
        label: 'kulutus',
        value: hoveredConsumption.comparison.v,
        unit,
        decimals: this.getUsageDecimals(unit),
      })
    }

    if (showTemperature && hoveredTemperature) {
      consumptionTooltipRows.push({
        ts: hoveredTemperature.ts,
        label: 'lämpötila',
        value: Number(hoveredTemperature.v),
        unit: '°C',
        decimals: 1,
      })
    }

    if (showComparison && showTemperature && hoveredTemperature?.comparison) {
      consumptionTooltipRows.push({
        ts: hoveredTemperature.comparison.ts,
        label: 'lämpötila',
        value: Number(hoveredTemperature.comparison.v),
        unit: '°C',
        decimals: 1,
      })
    }

    const productionTooltipRows: BarChartTooltipRow[] = []

    if (hoveredProduction && hoveredProduction.vp !== undefined) {
      productionTooltipRows.push({
        ts: hoveredProduction.ts,
        label: 'tuotanto',
        value: Number(hoveredProduction.vp),
        unit,
        decimals: this.getUsageDecimals(unit),
      })
    }

    if (
      showComparison &&
      hoveredProduction?.comparison &&
      hoveredProduction.comparison.vp !== undefined
    ) {
      productionTooltipRows.push({
        ts: hoveredProduction.comparison.ts,
        label: 'tuotanto',
        value: Number(hoveredProduction.comparison.vp),
        unit,
        decimals: this.getUsageDecimals(unit),
      })
    }

    return (
      <>
        {/* Consumption data barchart */}
        <div className="max-width-parent">
          <div className="nested-grid">
            <svg width={55} height={svgDimensions.height}>
              <Axes
                scales={{ xScale, yScale, yScale3 }}
                margins={{ top: 60, right: 0, bottom: 60, left: 50 }}
                svgDimensions={svgDimensions}
                unit={unit}
                location={id}
                timeframe={timeframe}
                sum={sum}
                data={data}
                tick={tick}
                type={'consumption'}
                showTemperatureData={showTemperature}
                contractFromId={contractFromId}
              />
            </svg>

            <div
              className={scrollable || loading ? 'scroll-box scroll' : 'scroll-box'}
              ref={this.consumptionScrollDiv}
            >
              <div className="scroll-graph">
                <svg
                  id="consumption"
                  width={calculateBarSvgWidth()}
                  height={barsSvgDimensions.height}
                >
                  <Bars
                    scales={{ xScale, yScale }}
                    margins={barMargins}
                    data={data}
                    usageData={usageData}
                    updateUsageData={updateUsageData}
                    maxValue={maxConsumptionScaleValue}
                    svgDimensions={barsSvgDimensions}
                    unit={unit}
                    tick={tick}
                    showComparison={showComparison}
                    showTemperature={showTemperature}
                    isProductionData={false}
                    maximumResolution={maximumResolution}
                    loading={loading}
                    onBarHover={(ts, x) => this.setHoveredConsumptionSlot({ ts, x })}
                    onBarLeave={() => this.setHoveredConsumptionSlot(null)}
                  />

                  {showTemperature && usageData && (
                    <Lines
                      scales={{ xScale, yScale, yScale3 }}
                      margins={barMargins}
                      data={slicedTempData}
                      svgDimensions={svgDimensions}
                      showComparison={showComparison}
                      level={usageData.level}
                    />
                  )}

                  <BarChartTooltip
                    hoveredSlot={hoveredConsumptionSlot}
                    rows={consumptionTooltipRows}
                    svgWidth={barsSvgDimensions.width}
                    tick={tick}
                  />
                </svg>
              </div>
            </div>
          </div>
          {/* temperature axis */}
          <svg width={55} height={svgDimensions.height}>
            <Axes
              scales={{ xScale, yScale, yScale3 }}
              margins={{ top: 60, right: 50, bottom: 60, left: 0 }}
              svgDimensions={svgDimensions}
              unit={unit}
              location={id}
              timeframe={timeframe}
              sum={sum}
              data={data}
              tick={tick}
              type={'temperature'}
              showTemperatureData={showTemperature}
              contractFromId={contractFromId}
            />
          </svg>
        </div>
        {/* Also return an svg for electricity production data if available */}
        <div
          id="production"
          className={
            usageData?.type === 'electricity' && maxProductionValue > 0.01
              ? 'max-width-parent'
              : 'max-width-parent hide'
          }
        >
          <div className="nested-grid">
            <svg width={55} height={svgDimensions.height}>
              <Axes
                scales={{ xScale, yScale, yScale2 }}
                margins={{ top: 60, right: 0, bottom: 60, left: 50 }}
                svgDimensions={svgDimensions}
                unit={unit}
                location={id}
                timeframe={timeframe}
                sum={sum}
                data={data}
                tick={tick}
                type={'production'}
                showTemperatureData={false}
                contractFromId={contractFromId}
              />
            </svg>

            <div
              className={
                scrollable || loading ? 'scroll-box scroll no-scrollbar-show' : 'scroll-box no-scrollbar-show'
              }
              ref={this.productionScrollDiv}
            >
              <div className="scroll-graph">
                <svg width={barsSvgDimensions.width} height={barsSvgDimensions.height}>
                  <Bars
                    scales={{ xScale, yScale, yScale2 }}
                    margins={barMargins}
                    data={data}
                    usageData={usageData}
                    updateUsageData={updateUsageData}
                    maxValue={maxProductionScaleValue}
                    svgDimensions={barsSvgDimensions}
                    unit={unit}
                    tick={tick}
                    showComparison={showComparison}
                    showTemperature={false}
                    isProductionData={true}
                    maximumResolution={maximumResolution}
                    loading={loading}
                    onBarHover={(ts, x) => this.setHoveredProductionSlot({ ts, x })}
                    onBarLeave={() => this.setHoveredProductionSlot(null)}
                  />
                  <BarChartTooltip
                    hoveredSlot={hoveredProductionSlot}
                    rows={productionTooltipRows}
                    svgWidth={barsSvgDimensions.width}
                    svgHeight={barsSvgDimensions.height}
                    tick={tick}
                    placement="bottom"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {usageData?.type === 'electricity' && maxProductionValue <= 0.01 && (
          <p>Sähköntuotannon tieto ei ole saatavilla tälle ajanjaksolle</p>
        )}
      </>
    )
  }
}

export default responsive(BarChart)